import type { Delay } from './calendar.js';

export const CHANNELS = ['email', 'in_app', 'whatsapp', 'sms'] as const;
export type Channel = (typeof CHANNELS)[number];
/** Channels implemented in the initial release. WhatsApp/SMS require MOD11 + explicit consent. */
export const ACTIVE_CHANNELS: Channel[] = ['email', 'in_app'];

export const TRIGGERS = [
  'invitation.sent',
  'engagement.not_started',
  'form.incomplete',
  'engagement.due_soon',
  'engagement.overdue',
  'evidence.missing',
  'review.sla',
  'approval.pending',
  'access.expiring',
  'engagement.completed',
] as const;
export type TriggerType = (typeof TRIGGERS)[number];

export const RECIPIENT_RESOLVERS = [
  'invitee',
  'client_admins',
  'client_contributors_assigned',
  'client_approvers',
  'engagement_owner',
  'account_manager',
  'assigned_reviewers',
  'tenant_admins',
] as const;
export type RecipientResolver = (typeof RECIPIENT_RESOLVERS)[number];

export const STOP_CONDITIONS = [
  'invitation_accepted',
  'invitation_revoked',
  'form_started',
  'forms_submitted',
  'items_resolved',
  'review_actioned',
  'approval_decided',
  'access_renewed',
  'due_date_changed',
] as const;
export type StopCondition = (typeof STOP_CONDITIONS)[number];

export type Audience = 'client' | 'internal';

export interface AutomationRuleDef {
  code: string;
  name: string;
  description?: string;
  enabled: boolean;
  trigger: TriggerType;
  audience: Audience;
  recipients: RecipientResolver[];
  channels: Channel[];
  messageKey: string;
  /** Initial delay after the trigger instant (or before due date for due_soon, as negative days). */
  delay: Delay;
  /** For due-relative triggers: offset from due date in business days (negative = before). */
  dueOffsetBusinessDays?: number;
  businessHoursOnly: boolean;
  respectQuietHours: boolean;
  /** Maximum occurrences for a recipient per engagement+item. */
  maxOccurrences: number;
  /** Interval between repeated occurrences. */
  repeatEvery?: Delay;
  /** Minimum hours between any two client messages to the same recipient (digesting happens inside this window). */
  cooldownHours: number;
  priority: 'low' | 'normal' | 'high';
  escalation?: { afterOccurrences: number; recipients: RecipientResolver[] };
  stopConditions: StopCondition[];
  /** Only for review.sla */
  slaBusinessDays?: number;
  /** Only for access.expiring */
  daysBeforeExpiry?: number;
}

export const MESSAGE_VARIABLES = [
  'recipient_first_name',
  'tenant_name',
  'client_name',
  'engagement_name',
  'due_date',
  'portal_link',
  'missing_count',
  'missing_summary',
  'review_link',
  'approval_link',
  'access_provider',
  'expiry_date',
  'sender_name',
  'occurrence',
] as const;
export type MessageVariable = (typeof MESSAGE_VARIABLES)[number];

export interface MessageTemplateDef {
  key: string;
  subject: string;
  body: string;
  /** Short in-app body */
  inApp: string;
}

export const DEFAULT_MESSAGES: MessageTemplateDef[] = [
  {
    key: 'invitation',
    subject: '{{tenant_name}} invited you to complete onboarding for {{client_name}}',
    body: 'Hi {{recipient_first_name}},\n\n{{tenant_name}} has invited you to the onboarding workspace for {{client_name}} ({{engagement_name}}).\n\nOpen your secure invitation: {{portal_link}}\n\nThis invitation link is personal and expires. Never share passwords — access to your platforms is requested through proper roles.',
    inApp: 'You were invited to {{engagement_name}}.',
  },
  {
    key: 'invitation_reminder',
    subject: 'Reminder: your onboarding invitation for {{client_name}}',
    body: 'Hi {{recipient_first_name}},\n\nYour invitation to {{engagement_name}} is still waiting. Accept it here: {{portal_link}}',
    inApp: 'Your invitation to {{engagement_name}} is pending.',
  },
  {
    key: 'not_started',
    subject: 'Get started with {{engagement_name}}',
    body: 'Hi {{recipient_first_name}},\n\nOnboarding for {{client_name}} has not been started yet. It is due {{due_date}}.\n\nContinue: {{portal_link}}',
    inApp: '{{engagement_name}} has not been started.',
  },
  {
    key: 'incomplete',
    subject: '{{missing_count}} items still needed for {{engagement_name}}',
    body: 'Hi {{recipient_first_name}},\n\nA few items are still needed: {{missing_summary}}.\n\nContinue where you left off: {{portal_link}}',
    inApp: '{{missing_count}} items still needed.',
  },
  {
    key: 'due_soon',
    subject: '{{engagement_name}} is due {{due_date}}',
    body: 'Hi {{recipient_first_name}},\n\nOnboarding for {{client_name}} is due {{due_date}}. Outstanding: {{missing_summary}}.\n\n{{portal_link}}',
    inApp: 'Due {{due_date}}: {{missing_count}} items outstanding.',
  },
  {
    key: 'overdue',
    subject: 'Overdue: {{engagement_name}}',
    body: 'Hi {{recipient_first_name}},\n\nOnboarding for {{client_name}} was due {{due_date}}. Outstanding: {{missing_summary}}.\n\n{{portal_link}}',
    inApp: '{{engagement_name}} is overdue.',
  },
  {
    key: 'missing_evidence',
    subject: 'Assets or access still needed for {{engagement_name}}',
    body: 'Hi {{recipient_first_name}},\n\nWe still need: {{missing_summary}}.\n\nPlease grant access using the requested roles — never share passwords.\n\n{{portal_link}}',
    inApp: 'Assets/access still needed: {{missing_count}}.',
  },
  {
    key: 'review_sla',
    subject: 'Review waiting: {{engagement_name}}',
    body: 'A submission for {{client_name}} is waiting for review.\n\n{{review_link}}',
    inApp: 'Submission waiting for review.',
  },
  {
    key: 'approval',
    subject: 'Approval requested: {{engagement_name}}',
    body: 'Hi {{recipient_first_name}},\n\nPlease review and approve the onboarding submission for {{client_name}}.\n\n{{approval_link}}',
    inApp: 'Your approval is requested.',
  },
  {
    key: 'access_expiry',
    subject: '{{access_provider}} access expires {{expiry_date}}',
    body: 'Access to {{access_provider}} for {{client_name}} expires {{expiry_date}}.\n\n{{portal_link}}',
    inApp: '{{access_provider}} access expires {{expiry_date}}.',
  },
  {
    key: 'completion',
    subject: 'Onboarding complete: {{engagement_name}}',
    body: 'Hi {{recipient_first_name}},\n\nOnboarding for {{client_name}} is complete. Thank you!\n\n{{portal_link}}',
    inApp: 'Onboarding for {{engagement_name}} is complete.',
  },
];

const base = {
  enabled: true,
  businessHoursOnly: true,
  respectQuietHours: true,
  priority: 'normal' as const,
};

export const DEFAULT_AUTOMATION_RULES: AutomationRuleDef[] = [
  {
    ...base,
    code: 'AUT01',
    name: 'Invitation',
    trigger: 'invitation.sent',
    audience: 'client',
    recipients: ['invitee'],
    channels: ['email'],
    messageKey: 'invitation_reminder',
    delay: { amount: 2, unit: 'business_days' },
    maxOccurrences: 2,
    repeatEvery: { amount: 3, unit: 'business_days' },
    cooldownHours: 24,
    stopConditions: ['invitation_accepted', 'invitation_revoked'],
  },
  {
    ...base,
    code: 'AUT02',
    name: 'Not started',
    trigger: 'engagement.not_started',
    audience: 'client',
    recipients: ['client_admins', 'client_contributors_assigned'],
    channels: ['email', 'in_app'],
    messageKey: 'not_started',
    delay: { amount: 3, unit: 'business_days' },
    maxOccurrences: 2,
    repeatEvery: { amount: 3, unit: 'business_days' },
    cooldownHours: 24,
    stopConditions: ['form_started'],
  },
  {
    ...base,
    code: 'AUT03',
    name: 'Incomplete',
    trigger: 'form.incomplete',
    audience: 'client',
    recipients: ['client_admins', 'client_contributors_assigned'],
    channels: ['email', 'in_app'],
    messageKey: 'incomplete',
    delay: { amount: 4, unit: 'business_days' },
    maxOccurrences: 3,
    repeatEvery: { amount: 4, unit: 'business_days' },
    cooldownHours: 24,
    stopConditions: ['forms_submitted'],
  },
  {
    ...base,
    code: 'AUT04',
    name: 'Due soon',
    trigger: 'engagement.due_soon',
    audience: 'client',
    recipients: ['client_admins', 'client_contributors_assigned'],
    channels: ['email', 'in_app'],
    messageKey: 'due_soon',
    delay: { amount: 0, unit: 'minutes' },
    dueOffsetBusinessDays: -2,
    maxOccurrences: 1,
    cooldownHours: 24,
    stopConditions: ['forms_submitted', 'due_date_changed'],
  },
  {
    ...base,
    code: 'AUT05',
    name: 'Overdue',
    trigger: 'engagement.overdue',
    audience: 'client',
    recipients: ['client_admins'],
    channels: ['email', 'in_app'],
    messageKey: 'overdue',
    delay: { amount: 1, unit: 'business_days' },
    maxOccurrences: 3,
    repeatEvery: { amount: 3, unit: 'business_days' },
    cooldownHours: 48,
    priority: 'high',
    escalation: { afterOccurrences: 2, recipients: ['account_manager'] },
    stopConditions: ['forms_submitted', 'due_date_changed'],
  },
  {
    ...base,
    code: 'AUT06',
    name: 'Missing assets/access',
    trigger: 'evidence.missing',
    audience: 'client',
    recipients: ['client_admins', 'client_contributors_assigned'],
    channels: ['email', 'in_app'],
    messageKey: 'missing_evidence',
    delay: { amount: 3, unit: 'business_days' },
    maxOccurrences: 3,
    repeatEvery: { amount: 4, unit: 'business_days' },
    cooldownHours: 24,
    stopConditions: ['items_resolved'],
  },
  {
    ...base,
    code: 'AUT07',
    name: 'Review SLA',
    trigger: 'review.sla',
    audience: 'internal',
    recipients: ['assigned_reviewers', 'engagement_owner'],
    channels: ['email', 'in_app'],
    messageKey: 'review_sla',
    delay: { amount: 0, unit: 'minutes' },
    slaBusinessDays: 2,
    maxOccurrences: 3,
    repeatEvery: { amount: 1, unit: 'business_days' },
    cooldownHours: 8,
    priority: 'high',
    escalation: { afterOccurrences: 2, recipients: ['account_manager', 'tenant_admins'] },
    stopConditions: ['review_actioned'],
  },
  {
    ...base,
    code: 'AUT08',
    name: 'Approval',
    trigger: 'approval.pending',
    audience: 'client',
    recipients: ['client_approvers'],
    channels: ['email', 'in_app'],
    messageKey: 'approval',
    delay: { amount: 2, unit: 'business_days' },
    maxOccurrences: 3,
    repeatEvery: { amount: 3, unit: 'business_days' },
    cooldownHours: 24,
    stopConditions: ['approval_decided'],
  },
  {
    ...base,
    code: 'AUT09',
    name: 'Access expiry',
    trigger: 'access.expiring',
    audience: 'internal',
    recipients: ['engagement_owner', 'account_manager'],
    channels: ['email', 'in_app'],
    messageKey: 'access_expiry',
    delay: { amount: 0, unit: 'minutes' },
    daysBeforeExpiry: 7,
    maxOccurrences: 1,
    cooldownHours: 24,
    stopConditions: ['access_renewed'],
  },
  {
    ...base,
    code: 'AUT10',
    name: 'Completion',
    trigger: 'engagement.completed',
    audience: 'client',
    recipients: ['client_admins'],
    channels: ['email', 'in_app'],
    messageKey: 'completion',
    delay: { amount: 5, unit: 'minutes' },
    maxOccurrences: 1,
    cooldownHours: 0,
    stopConditions: [],
  },
];

/** Transient retry schedule: max 5 attempts spread over < 24h, with ±20% jitter. */
export const RETRY_BASE_DELAYS_MS = [5 * 60_000, 30 * 60_000, 2 * 3_600_000, 6 * 3_600_000, 14 * 3_600_000];
export const MAX_TRANSIENT_ATTEMPTS = 5;
export const RETRY_WINDOW_MS = 24 * 3_600_000;

export function retryDelayMs(attempt: number, random: () => number = Math.random): number | null {
  if (attempt >= MAX_TRANSIENT_ATTEMPTS) return null;
  const baseDelay = RETRY_BASE_DELAYS_MS[attempt] ?? RETRY_BASE_DELAYS_MS[RETRY_BASE_DELAYS_MS.length - 1]!;
  const jitter = baseDelay * 0.2 * (random() * 2 - 1);
  return Math.round(baseDelay + jitter);
}

export function buildSendKey(parts: {
  tenantId: string;
  engagementId: string;
  ruleVersionId: string;
  itemOrDigest: string;
  recipientId: string;
  channel: Channel;
  occurrence: number;
}): string {
  return [parts.tenantId, parts.engagementId, parts.ruleVersionId, parts.itemOrDigest, parts.recipientId, parts.channel, parts.occurrence].join(':');
}

export function renderMessage(template: string, vars: Partial<Record<MessageVariable, string>>): string {
  return template.replace(/\{\{\s*([a-z_]+)\s*\}\}/g, (_m, name: string) => {
    if (!(MESSAGE_VARIABLES as readonly string[]).includes(name)) return '';
    return vars[name as MessageVariable] ?? '';
  });
}
